import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Star, Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Favoris de filtres réutilisable (vues enregistrées, PERSONNELLES).
 *
 * Props :
 *  - page      : clé de page (ex. "planning", "banque", "prospection")
 *  - current   : objet décrivant l'état actuel des filtres (sérialisé tel quel)
 *  - onApply   : (params) => void — appelé quand l'utilisateur applique un favori
 *  - describe  : (params) => string — libellé court affiché sous le nom (optionnel)
 *  - label     : libellé du bouton (défaut "Favoris")
 *  - buttonClassName / buttonVariant : personnalisation du déclencheur
 */
export function SavedViews({ page, current, onApply, describe, label = "Favoris", buttonClassName = "", buttonVariant = "outline" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites", { params: { page } });
      setItems(data || []);
    } catch { /* silencieux */ } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error("Donnez un nom au favori"); return; }
    setSaving(true);
    try {
      await api.post("/favorites", { page, name: n, params: current || {} });
      toast.success(`Favori « ${n} » enregistré`);
      setName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/favorites/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch { toast.error("Suppression impossible"); }
  };

  const apply = (fav) => {
    onApply?.(fav.params || {});
    setOpen(false);
    toast.success(`Vue « ${fav.name} » appliquée`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={buttonVariant} className={buttonClassName} data-testid="favorites-trigger">
          <Star className="mr-1.5 h-4 w-4" />{label}{items.length ? ` (${items.length})` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72" data-testid="favorites-popover">
        <div className="space-y-3">
          <div>
            <div className="mb-1.5 text-xs font-bold text-ink">Mes vues enregistrées</div>
            {loading ? (
              <div className="flex items-center gap-2 py-2 text-xs text-muted-ink"><Loader2 className="h-3.5 w-3.5 animate-spin" />Chargement…</div>
            ) : items.length === 0 ? (
              <p className="py-1 text-[11px] text-muted-ink">Aucun favori pour l'instant. Enregistrez la vue actuelle ci-dessous.</p>
            ) : (
              <ul className="max-h-56 space-y-1 overflow-y-auto" data-testid="favorites-list">
                {items.map((f) => (
                  <li key={f.id}>
                    <button onClick={() => apply(f)} data-testid={`favorite-apply-${f.id}`}
                      className="group flex w-full items-center gap-2 rounded-lg border border-[var(--border-soft)] px-2.5 py-1.5 text-left text-xs transition-colors hover:border-[var(--shariti-orange)] hover:bg-[var(--surface-2)]">
                      <Star className="h-3.5 w-3.5 shrink-0 fill-[var(--shariti-orange)] text-[var(--shariti-orange)]" />
                      <span className="min-w-0 flex-1">
                        <span className="block break-words font-semibold text-ink">{f.name}</span>
                        {describe && describe(f.params) ? <span className="block break-words text-[10px] text-muted-ink">{describe(f.params)}</span> : null}
                      </span>
                      <span onClick={(e) => remove(f.id, e)} role="button" aria-label="Supprimer le favori" data-testid={`favorite-delete-${f.id}`}
                        className="shrink-0 rounded p-1 text-muted-ink opacity-0 transition-opacity hover:bg-[rgba(214,69,69,0.12)] hover:text-[#D64545] group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-[var(--border-soft)] pt-2.5">
            <div className="mb-1.5 text-[11px] font-semibold text-muted-ink">Enregistrer la vue actuelle</div>
            <div className="flex items-center gap-1.5">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du favori"
                onKeyDown={(e) => { if (e.key === "Enter") save(); }} className="h-8 text-xs" data-testid="favorite-name-input" maxLength={80} />
              <Button size="sm" className="h-8 shrink-0 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" onClick={save} disabled={saving} data-testid="favorite-save-btn">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
